import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface SearchResult {
  id: string;
  type: 'diary' | 'material' | 'video' | 'quiz';
  title: string;
  description?: string;
  date?: string;
  subject?: string;
}

interface GlobalSearchModalProps {
  onClose: () => void;
}

export default function GlobalSearchModal({ onClose }: GlobalSearchModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'diary' | 'material' | 'video' | 'quiz'>('all');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length >= 2) {
      searchAll();
    } else {
      setResults([]);
    }
  }, [query, user]);

  const searchAll = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const searchTerm = `%${query.trim()}%`;
      
      // 日記検索：自分のもの + 公開されているもの
      let diaryQuery = supabase
        .from('diary_entries')
        .select('id, title, content, created_at, subject, is_public, user_id')
        .or(`title.ilike.${searchTerm},content.ilike.${searchTerm}`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (user) {
        // ログイン時：自分のもの または 公開のもの
        diaryQuery = diaryQuery.or(`user_id.eq.${user.id},is_public.eq.true`);
      } else {
        // 未ログイン時：公開のもののみ
        diaryQuery = diaryQuery.eq('is_public', true);
      }

      const { data: diaries } = await diaryQuery;

      // 資料検索：自分のもの + 公開されているもの
      let materialQuery = supabase
        .from('study_materials')
        .select('id, title, description, created_at, subject, is_public, user_id')
        .or(`title.ilike.${searchTerm},description.ilike.${searchTerm}`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (user) {
        materialQuery = materialQuery.or(`user_id.eq.${user.id},is_public.eq.true`);
      } else {
        materialQuery = materialQuery.eq('is_public', true);
      }

      const { data: materials } = await materialQuery;

      // 動画検索：自分のもの + 公開されているもの
      let videoQuery = supabase
        .from('lecture_videos')
        .select('id, title, description, created_at, subject, is_public, user_id')
        .or(`title.ilike.${searchTerm},description.ilike.${searchTerm}`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (user) {
        videoQuery = videoQuery.or(`user_id.eq.${user.id},is_public.eq.true`);
      } else {
        videoQuery = videoQuery.eq('is_public', true);
      }

      const { data: videos } = await videoQuery;

      // クイズ検索：すべてのクイズを検索対象に
      const { data: quizzes } = await supabase
        .from('quiz_questions')
        .select('id, question, answer, created_at, subject, user_id')
        .or(`question.ilike.${searchTerm},answer.ilike.${searchTerm}`)
        .order('created_at', { ascending: false })
        .limit(20);

      const allResults: SearchResult[] = [
        ...(diaries || []).map(d => ({
          id: d.id,
          type: 'diary' as const,
          title: d.title || '無題の日記',
          description: d.content?.substring(0, 100),
          date: d.created_at,
          subject: d.subject,
        })),
        ...(materials || []).map(m => ({
          id: m.id,
          type: 'material' as const,
          title: m.title,
          description: m.description?.substring(0, 100),
          date: m.created_at,
          subject: m.subject,
        })),
        ...(videos || []).map(v => ({
          id: v.id,
          type: 'video' as const,
          title: v.title,
          description: v.description?.substring(0, 100),
          date: v.created_at,
          subject: v.subject,
        })),
        ...(quizzes || []).map(q => ({
          id: q.id,
          type: 'quiz' as const,
          title: q.question,
          description: q.answer?.substring(0, 100),
          date: q.created_at,
          subject: q.subject,
        })),
      ];

      setResults(allResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = activeTab === 'all' 
    ? results 
    : results.filter(r => r.type === activeTab);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'diary': return 'ri-book-2-line';
      case 'material': return 'ri-file-text-line';
      case 'video': return 'ri-video-line';
      case 'quiz': return 'ri-question-answer-line';
      default: return 'ri-file-line';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'diary': return '日記';
      case 'material': return '資料';
      case 'video': return '動画';
      case 'quiz': return 'クイズ';
      default: return '';
    }
  };

  const getTypeBgColor = (type: string) => {
    switch (type) {
      case 'diary': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'material': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'video': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'quiz': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'diary':
        navigate('/diary');
        break;
      case 'material':
        navigate('/materials');
        break;
      case 'video':
        navigate('/videos');
        break;
      case 'quiz':
        navigate('/quiz');
        break;
    }
    onClose();
  };

  const getResultCount = (type: 'all' | 'diary' | 'material' | 'video' | 'quiz') => {
    if (type === 'all') return results.length;
    return results.filter(r => r.type === type).length;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[100] flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 mt-20 mb-10">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="日記、資料、動画、クイズを検索..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              title="閉じる"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              { key: 'all', label: 'すべて', icon: 'ri-apps-line' },
              { key: 'diary', label: '日記', icon: 'ri-book-2-line' },
              { key: 'material', label: '資料', icon: 'ri-file-text-line' },
              { key: 'video', label: '動画', icon: 'ri-video-line' },
              { key: 'quiz', label: 'クイズ', icon: 'ri-question-answer-line' },
            ].map((tab) => {
              const count = getResultCount(tab.key as any);
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                  {count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.key
                        ? 'bg-white/20'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mb-3"></div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">検索中...</p>
            </div>
          ) : query.trim().length < 2 ? (
            <div className="text-center py-12">
              <i className="ri-search-line text-6xl text-gray-300 dark:text-gray-700 mb-3"></i>
              <p className="text-gray-500 dark:text-gray-400 text-sm">2文字以上入力して検索してください</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-file-search-line text-6xl text-gray-300 dark:text-gray-700 mb-3"></i>
              <p className="text-gray-500 dark:text-gray-400 text-sm">「{query}」の検索結果が見つかりませんでした</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredResults.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full text-left p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 rounded-xl transition-all border border-gray-200 dark:border-gray-700 hover:border-teal-500/30 group cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg border ${getTypeBgColor(result.type)} shrink-0`}>
                      <i className={`${getTypeIcon(result.type)} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getTypeBgColor(result.type)} font-medium`}>
                          {getTypeLabel(result.type)}
                        </span>
                        {result.subject && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            {result.subject}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1 group-hover:text-teal-500 transition-colors">
                        {result.title}
                      </h3>
                      {result.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                          {result.description}
                        </p>
                      )}
                      {result.date && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {new Date(result.date).toLocaleDateString('ja-JP')}
                        </p>
                      )}
                    </div>
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all text-xl shrink-0"></i>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}